import React, { useState } from "react";
import { Check } from "lucide-react";
import { Insight } from "../../../../api/src/types/insights";
import { cn } from "../../lib/utils";
import { getIntegrationLabel } from "../../utils/integrationLabels";

interface InsightListCardProps {
  insight: Insight;
  isSelected: boolean;
  onToggle: (id: string) => void;
  providers?: string[]; // normalized provider keys if available
}

const getIntegrationIconSrc = (provider: string): string | null => {
  const map: Record<string, string> = {
    slack: "/assets/integrations/slack.svg",
    github: "/assets/integrations/github-white.svg",
    jira: "/assets/integrations/jira.svg",
    clickup: "/assets/integrations/clickup.svg",
    asana: "/assets/integrations/asana.png",
    trello: "/assets/integrations/trello.svg",
    notion: "/assets/integrations/notion.png",
    mixpanel: "/assets/integrations/mixpanel.svg",
    monday: "/assets/integrations/monday.svg",
    "google-sheets": "/assets/integrations/sheets.png",
    sheets: "/assets/integrations/sheets.png",
    drive: "/assets/integrations/drive.png",
    docs: "/assets/integrations/docs.png",
    stripe: "/assets/integrations/stripe.png",
    hubspot: "/assets/integrations/hubspot.svg",
    intercom: "/assets/integrations/intercom.svg",
    salesforce: "/assets/integrations/salesforce.svg",
    linear: "/assets/integrations/linear.png",
    zendesk: "/assets/integrations/zendesk.svg",
    typeform: "/assets/integrations/typeform.svg",
    twitter: "/assets/integrations/twitter.svg",
    google_contacts: "/assets/integrations/google-contacts.svg",
    gitlab: "/assets/integrations/gitlab.svg",
    google: "/assets/integrations/google.svg",
  };
  return map[provider] || null;
};

const formatMetricValue = (dp?: any): string | null => {
  if (!dp || typeof dp.value !== "number") return null;
  const display = dp.display || {};
  const prefix = display.prefix || (dp.format === "currency" ? "$" : "");
  const precision = display.precision ?? (dp.format === "percentage" ? 0 : 0);
  if (dp.format === "percentage") return `${dp.value.toFixed(precision)}%`;
  return `${prefix}${dp.value.toFixed(precision)}`;
};

const InsightListCard: React.FC<InsightListCardProps> = ({
  insight,
  isSelected,
  onToggle,
  providers = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const dp = insight.primary_dataset?.data_points?.[0] as any;
  const valueText = formatMetricValue(dp);

  return (
    <button
      type="button"
      onClick={() => onToggle(insight.id)}
      className={cn(
        "relative text-left border p-4 bg-black transition",
        isSelected
          ? "border-2 border-purple-500"
          : "border border-gray-800 hover:shadow-md"
      )}
    >
      <div className="absolute top-3 right-3 w-7 h-7 border flex items-center justify-center bg-black border-gray-700">
        {isSelected && <Check className="w-4 h-4 text-white" />}
      </div>

      {providers.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {providers.slice(0, 3).map((p, idx) => {
            const src = getIntegrationIconSrc(String(p));
            const label = getIntegrationLabel(String(p));
            return (
              <span
                key={`${p}-${idx}`}
                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-900 border border-gray-700 text-xs text-gray-300"
              >
                {src ? (
                  <img
                    src={src}
                    alt={label}
                    className="w-4 h-4 object-contain"
                  />
                ) : (
                  <span className="w-4 h-4 bg-gray-800 text-gray-400 text-[10px] flex items-center justify-center border border-gray-700">
                    {label.slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="max-w-[90px] truncate">{label}</span>
              </span>
            );
          })}
          {providers.length > 3 && (
            <span className="text-xs text-gray-400">
              +{providers.length - 3} more
            </span>
          )}
        </div>
      )}

      <div className="text-lg md:text-xl font-semibold text-white pr-10">
        {insight.summary?.headline || insight.title}
      </div>

      <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
        {insight.time_range_start && insight.time_range_end && (
          <span>
            {new Date(insight.time_range_start).toLocaleDateString()} —{" "}
            {new Date(insight.time_range_end).toLocaleDateString()}
          </span>
        )}
      </div>

      {valueText && (
        <div className="mt-2 text-3xl font-bold text-white">{valueText}</div>
      )}

      {insight.summary?.key_findings &&
        Array.isArray(insight.summary.key_findings) &&
        insight.summary.key_findings.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Findings
            </div>
            <ul className="mt-1 text-sm text-gray-300 list-disc list-inside space-y-1">
              {insight.summary.key_findings.slice(0, 3).map((k, i) => (
                <li key={i} className="leading-snug">
                  {String(k)}
                </li>
              ))}
            </ul>
          </div>
        )}

      {insight.summary?.narrative && (
        <div className="mt-2 text-sm text-gray-300">
          <span className={cn(!isExpanded && "line-clamp-2")}>
            {insight.summary.narrative}
          </span>
          <button
            type="button"
            className="ml-1 text-blue-400 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
          >
            {isExpanded ? "less" : "more"}
          </button>
        </div>
      )}

      {insight.summary?.recommendations &&
        Array.isArray(insight.summary.recommendations) &&
        insight.summary.recommendations.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Recommendations
            </div>
            <ul className="mt-1 text-sm text-gray-300 list-disc list-inside space-y-1">
              {insight.summary.recommendations.slice(0, 3).map((r, i) => (
                <li key={i} className="leading-snug">
                  {String(r)}
                </li>
              ))}
            </ul>
          </div>
        )}
    </button>
  );
};

export default InsightListCard;
